export interface CalculationApiInput {
  readonly mainApplicant: ApplicantInfo;
  readonly partner?: ApplicantInfo;
  readonly studyLoanMonthlyPayment?: number;
  readonly otherLoanMonthlyPayment?: number;
  readonly alimonyMonthlyPayment?: number;
}

export interface ApplicantInfo {
  readonly age: number;
  readonly income: ApplicantIncome;
  readonly pensionIncome?: number;
  readonly isDutchNationality: boolean;
  readonly expats?: Expats;
}

export interface ApplicantIncome {
  readonly income?: number;
  readonly contractType: ContractType;
}

export interface IndefiniteIncome extends ApplicantIncome {
  readonly income: number;
}

export interface FlexibleIncome extends ApplicantIncome {
  readonly contractDuration: FlexibleContractDuration;
  readonly hasIncomeOtherEmployer?: boolean;
  readonly income3YearsAgo?: number;
  readonly income2YearsAgo?: number;
  readonly income1YearAgo?: number;
  readonly incomeForecast?: number;
  readonly income?: number;
}

export interface SelfEmployedIncome extends ApplicantIncome {
  readonly contractDuration: SelfEmployedContractDuration;
  readonly hasIncomeOtherEmployer: boolean;
  readonly income3YearsAgo?: number;
  readonly income2YearsAgo?: number;
  readonly income1YearAgo?: number;
  readonly incomeForecast: number;
  readonly otherIncome3YearsAgo?: number;
  readonly otherIncome2YearsAgo?: number;
  readonly otherIncome1YearAgo?: number;
  readonly otherIncomeForecast?: number;
}

export interface FixedTermIncome extends ApplicantIncome {
  readonly hasIncomeOtherEmployer?: boolean;
  readonly income3YearsAgo?: number;
  readonly income2YearsAgo?: number;
  readonly income1YearAgo?: number;
  readonly incomeForecast?: number;
  readonly income?: number;
  readonly hasIntentieVerklaring?: boolean;
  readonly hasDeclarationOfIntent?: boolean;
}

export enum ContractType {
  Indefinite = 'indefinite',
  Flexible = 'flexible',
  SelfEmployed = 'selfEmployed',
  FixedTerm = 'fixedTerm',
  Temp = 'temp',

  NoIncome = 'noIncome'
}

export const NO_INCOME: ApplicantIncome = { contractType: ContractType.NoIncome };

export interface TempIncome extends ApplicantIncome {
  readonly contractDuration: TempContractDuration;
  readonly hasIncomeOtherEmployer?: boolean;
  readonly income3YearsAgo?: number;
  readonly income2YearsAgo?: number;
  readonly income1YearAgo?: number;
  readonly incomeForecast?: number;
  readonly income?: number;
  readonly hasPerspectiefVerklaring?: boolean;
  readonly hasStatementOfEmploymentProspects?: boolean;
}

export interface Expats {
  readonly residencyStatusType: ResidencyStatusType;
  readonly contractDuration?: ExpatContractDuration;
  readonly worksAtInternationalOrganisation?: boolean;
  readonly allowance?: number;
  readonly paysNativeTax?: boolean;
  readonly hasDiscount?: boolean;
  readonly discountDuration?: number;
}

export enum FlexibleContractDuration {
  ShorterThan2Years = 'ShorterThan2Years',
  LongerThan2Years = 'LongerThan2Years'
}

export enum SelfEmployedContractDuration {
  ShorterThan1Year = 'ShorterThan1Year',
  Between1And2Years = 'Between1And2Years',
  Between2And3Years = 'Between2And3Years',
  LongerThan3Years = 'LongerThan3Years'
}

export enum TempContractDuration {
  ShorterThan1Year = 'ShorterThan1Year',
  Between1And3Years = 'Between1And3Years',
  LongerThan3Years = 'LongerThan3Years'
}

export enum ContractDurationType {
  LessThan6Months = 'LessThan6Months',
  MoreThan6Months = 'MoreThan6Months',
  ShorterThan1Year = 'ShorterThan1Year',
  Between1And2Years = 'Between1And2Years',
  Between1And3Years = 'Between1And3Years',
  Between2And3Years = 'Between2And3Years',
  LongerThan3Years = 'LongerThan3Years',
  ShorterThan2Years = 'ShorterThan2Years',
  LongerThan2Years = 'LongerThan2Years'
}

export enum ExpatContractDuration {
  LessThan6Months = 'LessThan6Months',
  MoreThan6Months = 'MoreThan6Months'
}

export enum ResidencyStatusType {
  EuEerOrSwiss = 'EuEerOrSwiss',
  PermanentPermit = 'PermanentPermit',
  TemporaryPermit = 'TemporaryPermit'
}
